import { get } from 'lodash'
import { runInAction } from 'mobx'
import { requireWorkflowById } from '../../workflows/actions'
import { getCompactLst } from '../../../api/utils/api-compact'
import { PLAN_FOR_DELETION_NR_OF_DAYS, warnPt } from '../../../config'
import rootStore from '../..'
import { responseHasError } from '../../users/actions'
import { deleteLstItem } from '../../../api/utils/api-delete'
import dayjs from 'dayjs'
import { wrapWithLoading } from '../../../config/general-actions'

const uiLstWorkflowsStore = rootStore.uiLstWorkflows

/**
 * @async
 * @function actLoadWorkflows
 * @description Asynchronously loads workflows from the server and updates the UI list store.
 * @returns {Promise<void>}
 */
export const actLoadWorkflows = wrapWithLoading(
	async (get_modified = false) => {
		const params = {
			del: uiLstWorkflowsStore.del,
			pg: uiLstWorkflowsStore.pg,
			rpg: uiLstWorkflowsStore.rpg,
			search: uiLstWorkflowsStore.search,
			workflow_type: uiLstWorkflowsStore.filter?.workflow_type || '',
			status: uiLstWorkflowsStore.filter?.status || '',
			priority: uiLstWorkflowsStore.filter?.priority || '',
			user_uid: uiLstWorkflowsStore.filter?.user_uid || '',
			for_user_uid: uiLstWorkflowsStore.filter?.for_user_uid || '',
			get_modified: get_modified,
			orderBy: uiLstWorkflowsStore.filter?.orderBy,
			orderDir: uiLstWorkflowsStore.filter?.orderDir
		}

		const _response = await getCompactLst('workflows', params)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success' && !get_modified) {
			runInAction(() => {
				_actSync(
					get(_response, ['itms'], []),
					get(_response, ['nr'], 0)
				)
			})
		} else if (
			!hasError &&
			_response.status === 'success' &&
			get_modified
		) {
			return _actCheckIfListIsModified(
				get(_response, ['itms'], []),
				get(_response, ['nr'], 0)
			)
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Workflows' }
)

/**
 * @async
 * @function actLoadSpecificMonthWorkflows
 * @description Asynchronously loads workflows for a specific month from the server and updates the UI list store.
 *
 * @param {number} timestamp - The timestamp representing the month for which to load workflows.
 * @param {string} type - The type of calendar panel (e.g., "month", "year").
 * @param {boolean} [get_modified=false] - Flag to check modifications instead of full loading.
 *
 * @returns {Promise<void|Object>}
 */
export const actLoadSpecificMonthWorkflows = wrapWithLoading(
	async (timestamp, type, get_modified = false) => {
		const params = {
			del: uiLstWorkflowsStore.del,
			pg: uiLstWorkflowsStore.pg,
			rpg: uiLstWorkflowsStore.rpg,
			search: uiLstWorkflowsStore.search,
			workflow_type: uiLstWorkflowsStore.filter?.workflow_type || '',
			status: uiLstWorkflowsStore.filter?.status || '',
			priority: uiLstWorkflowsStore.filter?.priority || '',
			user_uid: uiLstWorkflowsStore.filter?.user_uid || '',
			for_user_uid: uiLstWorkflowsStore.filter?.for_user_uid || '',
			get_modified: get_modified,
			orderBy: uiLstWorkflowsStore.filter?.orderBy,
			orderDir: uiLstWorkflowsStore.filter?.orderDir,
			timestamp: timestamp,
			type: type
		}

		const _response = await getCompactLst('workflows', params)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success' && !get_modified) {
			runInAction(() => {
				_actSync(
					get(_response, ['itms'], []),
					get(_response, ['nr'], 0)
				)
			})
		} else if (
			!hasError &&
			_response.status === 'success' &&
			get_modified
		) {
			return _actCheckIfListIsModified(
				get(_response, ['itms'], []),
				get(_response, ['nr'], 0)
			)
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Calendar Workflows' }
)

/**
 * @async
 * @function actUndoOrDeleteWorkflow
 * @description Deletes or undoes the deletion of a workflow item.
 * @param {string} workflowId - The ID of the workflow item.
 * @param {boolean} status - True to mark for deletion, false to undo.
 * @param {boolean} [demo=false] - Demo flag.
 * @returns {Promise<boolean>}
 */
export const actUndoOrDeleteWorkflow = wrapWithLoading(
	async (workflowId, status = true, demo = false) => {
		if (demo) {
			runInAction(() => {
				uiLstWorkflowsStore.setIds(
					uiLstWorkflowsStore.ids.filter((id) => id !== workflowId)
				)
				uiLstWorkflowsStore.setNr(uiLstWorkflowsStore.nr - 1)
			})
			return true
		}

		const plan_for_deletion = dayjs()
			.add(PLAN_FOR_DELETION_NR_OF_DAYS['workflows'], 'day')
			.unix()
		const timestamp = status ? plan_for_deletion : false

		const _response = await deleteLstItem('workflows', {
			id: workflowId,
			to_delete: timestamp || false
		})
		const _storeItm = requireWorkflowById(workflowId)
		_storeItm.setToDelete(timestamp)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				_storeItm.setToDelete(timestamp || false)
				uiLstWorkflowsStore.setIds(
					uiLstWorkflowsStore.ids.filter((id) => id !== workflowId)
				)
				uiLstWorkflowsStore.setNr(uiLstWorkflowsStore.nr - 1)
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Del/Undo Workflow' }
)

/**
 * @function _actCheckIfListIsModified
 * @description Compares local workflow list with server data to determine if updates are required.
 *
 * @param {Array} srvItms - Array of workflow items from the server.
 * @param {number} nr - Count of items from the server.
 * @returns {boolean} - Returns true if IDs or modification timestamps differ from local store.
 */
const _actCheckIfListIsModified = (srvItms, nr) => {
	const listIsModified = uiLstWorkflowsStore.checkIfListIsModified(
		srvItms.map((it) => it.workflow_id),
		nr
	)
	if (listIsModified) {
		return true
	}

	for (const _srvItm of srvItms) {
		const _storeItm = requireWorkflowById(_srvItm.workflow_id)
		if (_storeItm.modified !== _srvItm.modified) {
			return true
		}
	}

	return false
}

/**
 * @function _actSync
 * @description Syncs the server items with the store items.
 * @param {Array} srvItms - Server items.
 * @param {number} nr - Number of items.
 */
const _actSync = (srvItms, nr) => {
	warnPt(srvItms, nr)
	runInAction(() => {
		for (const _srvItm of srvItms) {
			const _storeItm = requireWorkflowById(_srvItm.workflow_id)
			_storeItm.updateFromServer(_srvItm)
		}
		uiLstWorkflowsStore.setIds(srvItms.map((it) => it.workflow_id))
		uiLstWorkflowsStore.setNr(nr)
	})
}

/**
 * @async
 * @function actLoadCompletedWorkflows
 * @description Asynchronously loads completed workflows from the server and updates the UI list store.
 * @returns {Promise<void>}
 */
export const actLoadCompletedWorkflows = wrapWithLoading(
	async (get_modified = false) => {
		const params = {
			status: 'completed',
			for_user_uid: uiLstWorkflowsStore.filter?.for_user_uid || ''
		}

		const _response = await getCompactLst('workflows', params)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success' && !get_modified) {
			runInAction(() => {
				_actSync(
					get(_response, ['itms'], []),
					get(_response, ['nr'], 0)
				)
			})
		} else if (
			!hasError &&
			_response.status === 'success' &&
			get_modified
		) {
			return _actCheckIfListIsModified(
				get(_response, ['itms'], []),
				get(_response, ['nr'], 0)
			)
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Workflows' }
)
